import { ref } from "lit/directives/ref.js";

import { customElement, state } from "lit/decorators.js";
import { Api, User } from "../../api.js";
import { BaseElement } from "../../utils/ui-components.js";
import { createRef } from "lit/directives/ref.js";
import { html } from "lit";
import { deleteIcon } from "../../utils/icons.js";

@customElement("user-list")
export class UserList extends BaseElement {
    @state()
    users: User[] = [];

    @state()
    private loading = true;

    @state()
    private error = "";

    @state()
    private loadingMore = false;

    @state()
    private hasMore = true;

    @state()
    private currentPage = 0;

    private readonly pageSize = 20;
    private loadMoreRef = createRef<HTMLDivElement>();
    private observer?: IntersectionObserver;

    @state()
    private deleting?: string;

    async firstUpdated() {
        await this.loadUsers();

        // Set up infinite scroll
        this.observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && this.hasMore && !this.loading && !this.loadingMore) {
                this.loadUsers(true);
            }
        });

        if (this.loadMoreRef.value) {
            this.observer.observe(this.loadMoreRef.value);
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.observer?.disconnect();
    }

    private async loadUsers(loadMore = false) {
        if (loadMore) {
            this.loadingMore = true;
        } else {
            this.loading = true;
            this.currentPage = 0;
            this.users = [];
        }

        try {
            const result = await Api.getUsers(this.currentPage, this.pageSize);
            if (loadMore) {
                this.users = [...this.users, ...result.users];
            } else {
                this.users = result.users;
            }

            this.hasMore = (this.currentPage + 1) * this.pageSize < result.total;
            if (this.hasMore) this.currentPage++;
        } catch (e) {
            this.error = "Failed to load users";
        } finally {
            this.loading = false;
            this.loadingMore = false;
        }
    }

    private async handleDelete(user: User) {
        if (!confirm(`Are you sure you want to delete ${user.name}?`)) {
            return;
        }

        this.deleting = user.id;
        try {
            await Api.deleteUser(user.id);
            this.users = this.users.filter((u) => u.id !== user.id);
        } catch (e) {
            alert("Failed to delete user");
        } finally {
            this.deleting = undefined;
        }
    }

    render() {
        return html`
            <div class="bg-white rounded-lg shadow">
                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50">
                            <tr>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                                <th class="px-6 py-3"></th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            ${this.users.map(
                                (user) => html`
                                    <tr
                                        class="hover:bg-gray-50 cursor-pointer"
                                        @click=${() => {
                                            location.href = `/admin/users/${user.id}`;
                                        }}
                                    >
                                        <td class="px-6 py-4 whitespace-nowrap text-sm">${user.name}</td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm">${user.email}</td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm">
                                            <span
                                                class="px-2 py-1 text-xs rounded-full ${user.role === "admin"
                                                    ? "bg-purple-100 text-purple-800"
                                                    : "bg-gray-100 text-gray-800"}"
                                            >
                                                ${user.role}
                                            </span>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm">${new Date(user.created_at).toLocaleDateString()}</td>
                                        <td class="px-6 py-4 whitespace-nowrap text-right text-sm">
                                            <button
                                                @click=${(e: Event) => {
                                                    e.stopPropagation();
                                                    this.handleDelete(user);
                                                }}
                                                class="text-red-600 hover:text-red-900 disabled:opacity-50"
                                                ?disabled=${this.deleting === user.id}
                                            >
                                                ${this.deleting === user.id ? "Deleting..." : html`<i class="icon w-4 h-4">${deleteIcon}</i>`}
                                            </button>
                                        </td>
                                    </tr>
                                `
                            )}
                        </tbody>
                    </table>
                </div>
                ${this.hasMore
                    ? html`
                          <div ${ref(this.loadMoreRef)} class="p-4 flex justify-center">
                              ${this.loading ? html`<div class="text-gray-500">Loading...</div>` : html`<div class="h-8"></div>`}
                          </div>
                      `
                    : null}
            </div>
        `;
    }
}
